/**
 * Presentational primitives for the tracked-candidate surface (index + the
 * per-candidate timeline).
 *
 * Client-facing (CHRO/recruiter), so brand-styled with the local `C/DM/UI/MONO`
 * tokens (warm paper, ink neutrals, red accent, violet for the pending-fusion
 * state) — the same system the result screens and the evaluation-set surface
 * use — and plain-HR copy per the tone contract. Mirrors the `sets/ui.tsx` +
 * `sets/cells.tsx` precedent: a self-contained kit so the new surface doesn't
 * restyle the shared result components.
 */

import type {
  CandidateStatus,
  InformationPacketType,
  RoundOutcome,
  RoundStatus,
} from "@shared/labs/level-assessment-candidate";
import { FileText, Gauge, Linkedin, MessageSquare, Text, X } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { C, DM, MONO, UI } from "../results/ui-tokens";

/** Violet border for the carry-forward strip — the design's `--violet-bd`.
 *  Kept local: the shared `C` palette carries the violet fill/foreground but
 *  not this border tint. */
export const VIOLET_BD = "rgba(145,0,72,0.18)";
export const VIOLET_BG = "rgba(145,0,72,0.08)";

/** Two-letter initials avatar (ink square), the timeline + index avatar. */
export function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const text =
    parts.length === 0
      ? "?"
      : parts.length === 1
        ? parts[0].slice(0, 2).toUpperCase()
        : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (
    <div
      style={{
        flex: "0 0 auto",
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.28),
        background: C.ink,
        color: C.white,
        display: "grid",
        placeItems: "center",
        ...MONO,
        fontSize: Math.round(size * 0.32),
        fontWeight: 500,
        letterSpacing: "0.02em",
      }}
    >
      {text}
    </div>
  );
}

/** Generic pill. */
function Chip({
  children,
  fg,
  bg,
  border,
  style,
}: {
  children: ReactNode;
  fg: string;
  bg: string;
  border?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        ...UI,
        fontSize: 12.5,
        fontWeight: 500,
        lineHeight: 1.3,
        padding: "4px 10px",
        borderRadius: 999,
        color: fg,
        background: bg,
        border: border ?? "none",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function Dot({ color }: { color: string }) {
  return (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
      }}
    />
  );
}

/** Candidate lifecycle state — Word-Carries-State (design review 2026-07-18):
 *  the word itself in its canonical color, no pill, no dot, no icon. */
export function CandidateStatusChip({ status }: { status: CandidateStatus }) {
  const active = status === "active";
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: active ? C.green : C.ink50 }}>
      {active ? "Ativo" : "Arquivado"}
    </span>
  );
}

/** A round's status WORD, driven by the derived `outcome` so it never
 *  contradicts the round body: score in flight → coral `Avaliando…` (running)
 *  / `Finalizando…` (a ready round's score still settling); score lost →
 *  alert-red `Não concluída`; discarded → ink-muted `Abandonado`; neutral read
 *  → ink-primary `Analisado`; everything settled → forest-pulse `Pronto`.
 *  Word-Carries-State (design review 2026-07-18): the word itself, in its
 *  canonical color — no pill, no dot, no icon. */
export function RoundStatusChip({
  status,
  outcome,
}: {
  status: RoundStatus;
  outcome: RoundOutcome;
}) {
  const word: CSSProperties = { ...UI, fontSize: 12.5, fontWeight: 600 };
  if (outcome === "evaluating" || outcome === "pending") {
    return (
      <span style={{ ...word, color: C.red }}>
        {outcome === "pending" ? "Finalizando…" : "Avaliando…"}
      </span>
    );
  }
  if (outcome === "failed") {
    return <span style={{ ...word, color: C.alertRed }}>Não concluída</span>;
  }
  if (outcome === "no_new_signal") {
    return <span style={{ ...word, color: C.ink }}>Analisado</span>;
  }
  if (status === "abandoned") {
    return <span style={{ ...word, color: C.ink50 }}>Abandonado</span>;
  }
  return <span style={{ ...word, color: C.green }}>Pronto</span>;
}

/** Discipline chip (red-tinted) shown on the timeline header. */
export function DisciplineChip({ label }: { label: string }) {
  return (
    <Chip fg={C.red} bg={C.red5}>
      {label}
    </Chip>
  );
}

/** A level token pill (`L4 · Sênior`, or a dashed `—` for abstention). `red`
 *  tints it (the anchored current level); `big` for the header context. */
export function LevelPill({
  level,
  names,
  red = false,
  big = false,
}: {
  level: number | null;
  names?: Record<string, string> | null;
  red?: boolean;
  big?: boolean;
}) {
  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    ...MONO,
    fontWeight: 500,
    fontSize: big ? 14 : 13,
    padding: big ? "6px 13px" : "4px 11px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  };
  if (level == null) {
    return (
      <span
        style={{
          ...base,
          color: C.ink20,
          border: `1px dashed ${C.ink20}`,
          background: C.white,
        }}
      >
        —
      </span>
    );
  }
  const name = names?.[String(level)]?.trim();
  return (
    <span
      style={{
        ...base,
        color: red ? C.red : C.ink70,
        border: `1px solid ${red ? C.red : C.ink14}`,
        background: red ? C.red5 : C.white,
      }}
    >
      L{level}
      {name && (
        <span style={{ ...UI, fontWeight: 500, marginLeft: 7, color: C.ink50 }}>{name}</span>
      )}
    </span>
  );
}

/** Confidence bucket → tone + label. Reuses the app-wide 0.6/0.75 thresholds
 *  (from `confidenceTone`) so a candidate's bucket matches the report's, while
 *  carrying the design's labeled copy. */
export function ConfidenceChip({ value }: { value: number | null }) {
  if (value == null) {
    return (
      <Chip fg={C.ink50} bg={C.ink5}>
        <Gauge size={13} /> Confiança —
      </Chip>
    );
  }
  const pct = `${Math.round(value * 100)}%`;
  if (value >= 0.75) {
    return (
      <Chip fg={C.greenFg} bg={C.greenBg}>
        <Dot color={C.green} /> Confiança alta · {pct}
      </Chip>
    );
  }
  if (value >= 0.6) {
    return (
      <Chip fg={C.amberFg} bg={C.amberBg}>
        <Dot color={C.amberFg} /> Confiança média · {pct}
      </Chip>
    );
  }
  return (
    <Chip fg={C.ink50} bg={C.ink5}>
      <Dot color={C.ink50} /> Confiança baixa · {pct}
    </Chip>
  );
}

const PACKET_META: Record<InformationPacketType, { icon: typeof Linkedin; label: string }> = {
  linkedin: { icon: Linkedin, label: "LinkedIn" },
  cv: { icon: FileText, label: "CV" },
  interview_transcript: { icon: MessageSquare, label: "Transcrição de entrevista" },
  free_text: { icon: Text, label: "Texto livre" },
};

/** A typed evidence chip for a packet added at a round. */
export function PacketChip({
  type,
  label,
  linkedinUrl,
}: {
  type: InformationPacketType;
  label: string;
  linkedinUrl?: string | null;
}) {
  const meta = PACKET_META[type];
  const Icon = meta.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        ...UI,
        fontSize: 12.5,
        color: C.ink70,
        background: C.ink5,
        border: `1px solid ${C.ink10}`,
        borderRadius: 999,
        padding: "5px 12px 5px 10px",
      }}
      title={linkedinUrl ?? undefined}
    >
      <Icon size={14} style={{ color: C.ink50, flex: "0 0 auto" }} />
      {label || meta.label}
    </span>
  );
}

/** The mock's default label per packet type, for the add-round panel + create. */
export function defaultPacketLabel(type: InformationPacketType): string {
  return PACKET_META[type].label;
}

/** Compact overall-level movement chip for a scored fusion round: "L{from} →
 *  L{to}" ("Entrou em L{x}" when the axis was scored from abstention), plus a
 *  neutral-toned "salto de nível" badge when the move is anomalous (≥2 rungs).
 *  When the integer level HELD but the round did carry a movement record (the
 *  round is scored — the only caller renders this inside the scored branch —
 *  and touched at least one axis), renders a neutral "Nível estável · eixos
 *  atualizados" chip instead of nothing: internal per-axis movement must never
 *  be invisible just because the rounded overall level didn't move
 *  (docs/labs/level-assessment/multi-round-narrative-layer.md §2, S3). Renders
 *  nothing only when there's no movement record at all (round 1, legacy,
 *  no_new_signal). */
export function MovementChip({
  movement,
}: {
  movement:
    | { fromLevel: number | null; toLevel: number | null; anomalous: boolean }
    | null
    | undefined;
}) {
  if (!movement || movement.toLevel === null) return null;
  if (movement.fromLevel === movement.toLevel) {
    return (
      <Chip fg={C.ink70} bg={C.ink5}>
        Nível estável · eixos atualizados
      </Chip>
    );
  }
  const label =
    movement.fromLevel === null
      ? `Entrou em L${movement.toLevel}`
      : `L${movement.fromLevel} → L${movement.toLevel}`;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          ...MONO,
          fontSize: 12,
          fontWeight: 500,
          color: C.ink70,
          background: C.ink5,
          border: `1px solid ${C.ink10}`,
          borderRadius: 999,
          padding: "4px 11px",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {movement.anomalous && (
        <span style={{ ...UI, fontSize: 12, fontWeight: 600, color: C.amberFg }}>
          salto de nível
        </span>
      )}
    </span>
  );
}

/** Local keyframes for the surface's motion (pulse dot, evaluating ring, card
 *  fade-in, skeleton shimmer). Rendered once per screen. Honors reduced-motion:
 *  the animations are decorative, so we disable them there. */
export function CandidateKeyframes() {
  return (
    <style>{`
      @keyframes la-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
      @keyframes la-ring { 0%,100%{box-shadow:0 0 0 0 rgba(154,90,6,.28)} 50%{box-shadow:0 0 0 5px rgba(154,90,6,0)} }
      @keyframes la-fadeup { from{opacity:0; transform:translateY(8px)} to{opacity:1; transform:none} }
      @keyframes la-shimmer { 0%{background-position:100% 0} 100%{background-position:-100% 0} }
      @media (prefers-reduced-motion: reduce) {
        .la-anim { animation: none !important; }
      }
    `}</style>
  );
}

/** A shimmering skeleton block for the loading states. */
export function Skeleton({ style }: { style?: CSSProperties }) {
  return (
    <div
      className="la-anim"
      style={{
        background: `linear-gradient(90deg, ${C.ink5} 25%, ${C.ink10} 37%, ${C.ink5} 63%)`,
        backgroundSize: "400% 100%",
        animation: "la-shimmer 1.4s ease infinite",
        borderRadius: 6,
        ...style,
      }}
    />
  );
}

// ── Buttons ──────────────────────────────────────────────────────────────────

const BTN_BASE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  ...UI,
  fontSize: 14,
  fontWeight: 500,
  padding: "11px 18px",
  borderRadius: 999,
  border: "none",
  cursor: "pointer",
  transition: "background .14s, border-color .14s, color .14s",
};

export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        background: disabled ? C.ink20 : C.red,
        color: C.white,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        background: C.white,
        color: C.ink70,
        border: `1px solid ${C.ink14}`,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

// ── Form fields ──────────────────────────────────────────────────────────────

const INPUT_STYLE: CSSProperties = {
  width: "100%",
  ...UI,
  fontSize: 14,
  color: C.ink,
  background: C.white,
  border: `1px solid ${C.ink16}`,
  borderRadius: 10,
  padding: "11px 13px",
  outline: "none",
};

export function Field({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ ...UI, fontSize: 13, fontWeight: 500, color: C.ink70, marginBottom: 6 }}>
        {label}
      </div>
      {children}
      {hint && (
        <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 6, lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={INPUT_STYLE}
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={5}
      style={{ ...INPUT_STYLE, resize: "vertical", minHeight: 96, lineHeight: 1.5 }}
    />
  );
}

// ── Overlay chrome ───────────────────────────────────────────────────────────

/** Centered modal over a dimmed backdrop. Click-outside + Escape close it. */
export function ModalShell({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <Backdrop onClose={onClose} align="center">
      <div
        className="la-anim"
        style={{
          width: "min(480px, 92vw)",
          maxHeight: "90vh",
          overflow: "auto",
          background: C.white,
          borderRadius: 18,
          boxShadow: `0 24px 60px ${C.ink28}`,
          padding: 26,
          position: "relative",
          animation: "la-fadeup .2s ease both",
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </Backdrop>
  );
}

/** Right-hand slide-over panel (the add-round surface). */
export function SlideoverShell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Backdrop onClose={onClose} align="end">
      <div
        style={{
          height: "100%",
          width: "min(460px, 94vw)",
          background: C.white,
          boxShadow: "-12px 0 48px rgba(31,27,23,.18)",
          overflow: "auto",
          padding: 26,
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </Backdrop>
  );
}

function Backdrop({
  onClose,
  align,
  children,
}: {
  onClose: () => void;
  align: "center" | "end";
  children: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-dismiss is a mouse convenience; Escape (wired by the caller) is the keyboard path
    // biome-ignore lint/a11y/useKeyWithClickEvents: see above
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: C.ink28,
        backdropFilter: "blur(2px)",
        zIndex: 60,
        display: "flex",
        alignItems: align === "center" ? "center" : "stretch",
        justifyContent: align === "center" ? "center" : "flex-end",
      }}
    >
      {children}
    </div>
  );
}

/** Modal/slideover title + subtitle + a close (×) button. */
export function OverlayHeader({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar"
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          border: "none",
          background: "none",
          color: C.ink50,
          cursor: "pointer",
        }}
      >
        <X size={20} />
      </button>
      <h3 style={{ ...DM, fontSize: 19, fontWeight: 600, color: C.ink, margin: "0 0 4px" }}>
        {title}
      </h3>
      <div style={{ ...UI, fontSize: 14, color: C.ink50, marginBottom: 20, lineHeight: 1.5 }}>
        {subtitle}
      </div>
    </>
  );
}

/** Right-aligned action row for modals/slideovers. */
export function OverlayActions({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
      {children}
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  return <div style={{ ...UI, fontSize: 13, color: C.red, marginTop: 4 }}>{children}</div>;
}

/** Re-export the brand tokens so consumers import styling from one place. */
export { C, DM, MONO, UI };
